import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { CreditCard, Settings as SettingsIcon, CheckCircle, XCircle, Eye, EyeOff, Shield, AlertTriangle, MessageSquare, Bell, FileText, Send, Receipt, Building2, Phone, Mail, Hash, FileCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Settings() {
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [whatsappSettings, setWhatsappSettings] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showWASecret, setShowWASecret] = useState(false);
  
  const [razorpayForm, setRazorpayForm] = useState({
    razorpay_key_id: '',
    razorpay_key_secret: ''
  });

  const [whatsappForm, setWhatsappForm] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_whatsapp_number: '',
    enable_invoice_sending: false,
    enable_refill_reminders: false
  });

  const [invoiceForm, setInvoiceForm] = useState({
    pharmacy_name: '',
    pharmacy_address: '',
    pharmacy_phone: '',
    pharmacy_email: '',
    gst_number: '',
    drug_license_number: '',
    logo_url: '',
    footer_text: 'Thank you for your purchase! Get well soon.',
    show_gst_breakdown: true,
    show_drug_license: true
  });

  useEffect(function() {
    fetchAllSettings();
  }, []);

  async function fetchAllSettings() {
    try {
      const [payRes, waRes, invRes] = await Promise.all([
        axios.get(`${API}/payment-settings`),
        axios.get(`${API}/whatsapp-settings`),
        axios.get(`${API}/invoice-settings`)
      ]);
      setPaymentSettings(payRes.data);
      setWhatsappSettings(waRes.data);
      setInvoiceSettings(invRes.data);
      
      if (payRes.data.razorpay_key_id) {
        setRazorpayForm({
          razorpay_key_id: payRes.data.razorpay_key_id,
          razorpay_key_secret: ''
        });
      }
      
      if (waRes.data.twilio_account_sid) {
        setWhatsappForm({
          twilio_account_sid: waRes.data.twilio_account_sid,
          twilio_auth_token: '',
          twilio_whatsapp_number: waRes.data.twilio_whatsapp_number || '',
          enable_invoice_sending: waRes.data.enable_invoice_sending || false,
          enable_refill_reminders: waRes.data.enable_refill_reminders || false
        });
      }
      
      // Set invoice form
      if (invRes.data) {
        setInvoiceForm({
          pharmacy_name: invRes.data.pharmacy_name || '',
          pharmacy_address: invRes.data.pharmacy_address || '',
          pharmacy_phone: invRes.data.pharmacy_phone || '',
          pharmacy_email: invRes.data.pharmacy_email || '',
          gst_number: invRes.data.gst_number || '',
          drug_license_number: invRes.data.drug_license_number || '',
          logo_url: invRes.data.logo_url || '',
          footer_text: invRes.data.footer_text || 'Thank you for your purchase! Get well soon.',
          show_gst_breakdown: invRes.data.show_gst_breakdown !== false,
          show_drug_license: invRes.data.show_drug_license !== false
        });
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  async function saveRazorpaySettings(e) {
    e.preventDefault();
    if (!razorpayForm.razorpay_key_id) {
      toast.error('Please enter Razorpay Key ID');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/payment-settings`, razorpayForm);
      toast.success('Razorpay settings saved successfully');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  async function disconnectRazorpay() {
    setSaving(true);
    try {
      await axios.post(`${API}/payment-settings`, {
        razorpay_key_id: '',
        razorpay_key_secret: ''
      });
      setRazorpayForm({ razorpay_key_id: '', razorpay_key_secret: '' });
      toast.success('Razorpay disconnected');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setSaving(false);
    }
  }

  async function saveWhatsappSettings(e) {
    e.preventDefault();
    if (!whatsappForm.twilio_account_sid) {
      toast.error('Please enter Twilio Account SID');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/whatsapp-settings`, whatsappForm);
      toast.success('WhatsApp settings saved successfully');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to save WhatsApp settings');
    } finally {
      setSaving(false);
    }
  }

  async function disconnectWhatsapp() {
    setSaving(true);
    try {
      await axios.post(`${API}/whatsapp-settings`, {
        twilio_account_sid: '',
        twilio_auth_token: '',
        twilio_whatsapp_number: '',
        enable_invoice_sending: false,
        enable_refill_reminders: false
      });
      setWhatsappForm({
        twilio_account_sid: '',
        twilio_auth_token: '',
        twilio_whatsapp_number: '',
        enable_invoice_sending: false,
        enable_refill_reminders: false
      });
      toast.success('WhatsApp disconnected');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setSaving(false);
    }
  }

  async function saveInvoiceSettings(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/invoice-settings`, invoiceForm);
      toast.success('Invoice settings saved successfully');
      fetchAllSettings();
    } catch (error) {
      toast.error('Failed to save invoice settings');
    } finally {
      setSaving(false);
    }
  }

  function getStatusBadge(status) {
    if (status === 'connected') {
      return (
        <Badge className="bg-mint-100 text-mint-800 border-mint-300">
          <CheckCircle className="w-3 h-3 mr-1" /> Connected
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-600 border-slate-300">
        <XCircle className="w-3 h-3 mr-1" /> Not Connected
      </Badge>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mint-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Settings</h1>
        <p className="text-slate-500 text-sm">Manage your pharmacy settings and integrations</p>
      </div>

      <Tabs defaultValue="invoice" className="space-y-4">
        <TabsList>
          <TabsTrigger value="invoice" data-testid="tab-invoice">
            <Receipt className="w-4 h-4 mr-2" /> Invoice
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            <CreditCard className="w-4 h-4 mr-2" /> Payments
          </TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
            <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
          </TabsTrigger>
        </TabsList>

        {/* Invoice Settings Tab */}
        <TabsContent value="invoice">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-mint-600" />
                  Invoice Customization
                </CardTitle>
                <CardDescription>Customize how your invoices look when printed or shared</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={saveInvoiceSettings} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="pharmacy_name" className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      Pharmacy Name
                    </Label>
                    <Input
                      id="pharmacy_name"
                      data-testid="invoice-pharmacy-name"
                      placeholder="Enter your pharmacy name"
                      value={invoiceForm.pharmacy_name}
                      onChange={function(e) { setInvoiceForm({ ...invoiceForm, pharmacy_name: e.target.value }); }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="pharmacy_address">Address</Label>
                    <Textarea
                      id="pharmacy_address"
                      data-testid="invoice-pharmacy-address"
                      placeholder="Enter complete address"
                      rows={2}
                      value={invoiceForm.pharmacy_address}
                      onChange={function(e) { setInvoiceForm({ ...invoiceForm, pharmacy_address: e.target.value }); }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="pharmacy_phone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-500" />
                        Phone
                      </Label>
                      <Input
                        id="pharmacy_phone"
                        data-testid="invoice-pharmacy-phone"
                        placeholder="+91 9876543210"
                        value={invoiceForm.pharmacy_phone}
                        onChange={function(e) { setInvoiceForm({ ...invoiceForm, pharmacy_phone: e.target.value }); }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pharmacy_email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-500" />
                        Email
                      </Label>
                      <Input
                        id="pharmacy_email"
                        data-testid="invoice-pharmacy-email"
                        type="email"
                        placeholder="pharmacy@email.com"
                        value={invoiceForm.pharmacy_email}
                        onChange={function(e) { setInvoiceForm({ ...invoiceForm, pharmacy_email: e.target.value }); }}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="gst_number" className="flex items-center gap-2">
                        <Hash className="w-4 h-4 text-slate-500" />
                        GST Number
                      </Label>
                      <Input
                        id="gst_number"
                        data-testid="invoice-gst-number"
                        placeholder="22AAAAA0000A1Z5"
                        value={invoiceForm.gst_number}
                        onChange={function(e) { setInvoiceForm({ ...invoiceForm, gst_number: e.target.value }); }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="drug_license" className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-slate-500" />
                        Drug License No.
                      </Label>
                      <Input
                        id="drug_license"
                        data-testid="invoice-drug-license"
                        placeholder="DL-XX-XXXXXX"
                        value={invoiceForm.drug_license_number}
                        onChange={function(e) { setInvoiceForm({ ...invoiceForm, drug_license_number: e.target.value }); }}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="footer_text">Invoice Footer Message</Label>
                    <Input
                      id="footer_text"
                      data-testid="invoice-footer-text"
                      placeholder="Thank you message..."
                      value={invoiceForm.footer_text}
                      onChange={function(e) { setInvoiceForm({ ...invoiceForm, footer_text: e.target.value }); }}
                    />
                  </div>
                  
                  <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="show_gst" className="text-sm cursor-pointer">Show GST Breakdown on Invoice</Label>
                      <Switch
                        id="show_gst"
                        checked={invoiceForm.show_gst_breakdown}
                        onCheckedChange={function(checked) { setInvoiceForm({ ...invoiceForm, show_gst_breakdown: checked }); }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="show_license" className="text-sm cursor-pointer">Show Drug License Number</Label>
                      <Switch
                        id="show_license"
                        checked={invoiceForm.show_drug_license}
                        onCheckedChange={function(checked) { setInvoiceForm({ ...invoiceForm, show_drug_license: checked }); }}
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full bg-mint-600 hover:bg-mint-700"
                    disabled={saving}
                    data-testid="save-invoice-settings-btn"
                  >
                    {saving ? 'Saving...' : 'Save Invoice Settings'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Invoice Preview Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Invoice Preview</CardTitle>
                <CardDescription>How your invoice will look</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 bg-white">
                  <div className="text-center border-b pb-3 mb-3">
                    <h3 className="font-bold text-lg text-navy-900">
                      {invoiceForm.pharmacy_name || 'Your Pharmacy Name'}
                    </h3>
                    {invoiceForm.pharmacy_address && (
                      <p className="text-xs text-slate-500 mt-1 whitespace-pre-line">{invoiceForm.pharmacy_address}</p>
                    )}
                    <div className="flex justify-center gap-4 mt-2 text-xs text-slate-600">
                      {invoiceForm.pharmacy_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {invoiceForm.pharmacy_phone}
                        </span>
                      )}
                      {invoiceForm.pharmacy_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {invoiceForm.pharmacy_email}
                        </span>
                      )}
                    </div>
                    {(invoiceForm.gst_number || invoiceForm.drug_license_number) && (
                      <div className="flex justify-center gap-4 mt-2 text-xs text-slate-500">
                        {invoiceForm.gst_number && invoiceForm.show_gst_breakdown && (
                          <span>GST: {invoiceForm.gst_number}</span>
                        )}
                        {invoiceForm.drug_license_number && invoiceForm.show_drug_license && (
                          <span>DL: {invoiceForm.drug_license_number}</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs space-y-1 mb-3">
                    <p><strong>Invoice:</strong> INV-2026-0001</p>
                    <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                    <p><strong>Customer:</strong> John Doe</p>
                  </div>
                  
                  <table className="w-full text-xs mb-3">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Item</th>
                        <th className="text-right py-1">Qty</th>
                        <th className="text-right py-1">Price</th>
                        <th className="text-right py-1">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-dashed">
                        <td className="py-1">Paracetamol 500mg</td>
                        <td className="text-right">2</td>
                        <td className="text-right">₹25</td>
                        <td className="text-right">₹50</td>
                      </tr>
                      <tr className="border-b border-dashed">
                        <td className="py-1">Vitamin C</td>
                        <td className="text-right">1</td>
                        <td className="text-right">₹80</td>
                        <td className="text-right">₹80</td>
                      </tr>
                    </tbody>
                  </table>
                  
                  <div className="border-t pt-2 text-xs space-y-1">
                    <div className="flex justify-between"><span>Subtotal:</span><span>₹130</span></div>
                    {invoiceForm.show_gst_breakdown && (
                      <div className="flex justify-between text-slate-500"><span>GST (12%):</span><span>₹15.60</span></div>
                    )}
                    <div className="flex justify-between font-bold text-sm"><span>Total:</span><span>₹145.60</span></div>
                  </div>
                  
                  <div className="text-center mt-4 pt-3 border-t border-dashed">
                    <p className="text-xs text-slate-500">{invoiceForm.footer_text}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      Razorpay Integration
                    </CardTitle>
                    <CardDescription>Accept digital payments via UPI, cards, and wallets</CardDescription>
                  </div>
                  {getStatusBadge(paymentSettings?.onboarding_status)}
                </div>
              </CardHeader>
              <CardContent>
                {paymentSettings?.onboarding_status === 'connected' ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-mint-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-mint-600" />
                        <span className="font-medium text-mint-800">Razorpay Connected</span>
                      </div>
                      <p className="text-sm text-mint-700">
                        Your pharmacy can now accept digital payments. Use the "Digital Pay" button during billing.
                      </p>
                    </div>
                    
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <Label className="text-xs text-slate-500">Key ID</Label>
                      <p className="font-mono text-sm">{paymentSettings.razorpay_key_id}</p>
                    </div>
                    
                    <Button
                      variant="outline"
                      className="w-full text-red-600 border-red-300 hover:bg-red-50"
                      onClick={disconnectRazorpay}
                      disabled={saving}
                    >
                      Disconnect Razorpay
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={saveRazorpaySettings} className="space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800 text-sm">Setup Required</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Get your API keys from{' '}
                            <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noopener noreferrer" className="underline">
                              Razorpay Dashboard
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="key_id">Razorpay Key ID</Label>
                      <Input
                        id="key_id"
                        data-testid="razorpay-key-id"
                        placeholder="rzp_test_xxxxxxxxxxxxx"
                        value={razorpayForm.razorpay_key_id}
                        onChange={function(e) { setRazorpayForm({ ...razorpayForm, razorpay_key_id: e.target.value }); }}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="key_secret">Razorpay Key Secret</Label>
                      <div className="relative">
                        <Input
                          id="key_secret"
                          data-testid="razorpay-key-secret"
                          type={showSecret ? 'text' : 'password'}
                          placeholder="Enter your API secret"
                          value={razorpayForm.razorpay_key_secret}
                          onChange={function(e) { setRazorpayForm({ ...razorpayForm, razorpay_key_secret: e.target.value }); }}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={function() { setShowSecret(!showSecret); }}
                        >
                          {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={saving}
                      data-testid="connect-razorpay-btn"
                    >
                      {saving ? 'Connecting...' : 'Connect Razorpay'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Digital Payment Features</CardTitle>
                <CardDescription>What you get with Razorpay integration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-mint-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 text-mint-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy-900">UPI Payments</h4>
                    <p className="text-sm text-slate-500">Accept payments via Google Pay, PhonePe, Paytm</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy-900">Dynamic QR Codes</h4>
                    <p className="text-sm text-slate-500">Generate QR codes for each bill amount</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy-900">Real-time Status</h4>
                    <p className="text-sm text-slate-500">Instant payment confirmation notifications</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="whatsapp">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-green-600" />
                      WhatsApp Integration (Twilio)
                    </CardTitle>
                    <CardDescription>Send invoices and refill reminders via WhatsApp</CardDescription>
                  </div>
                  {getStatusBadge(whatsappSettings?.status)}
                </div>
              </CardHeader>
              <CardContent>
                {whatsappSettings?.status === 'connected' ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">WhatsApp Connected</span>
                      </div>
                      <p className="text-sm text-green-700">
                        You can now send invoices and refill reminders to customers via WhatsApp.
                      </p>
                    </div>
                    
                    <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                      <div>
                        <Label className="text-xs text-slate-500">Account SID</Label>
                        <p className="font-mono text-sm">{whatsappSettings.twilio_account_sid}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">WhatsApp Number</Label>
                        <p className="font-mono text-sm">{whatsappSettings.twilio_whatsapp_number || 'Sandbox'}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-600" />
                          <span className="text-sm">Send Invoices</span>
                        </div>
                        <Badge className={whatsappSettings.enable_invoice_sending ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}>
                          {whatsappSettings.enable_invoice_sending ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-slate-600" />
                          <span className="text-sm">Refill Reminders</span>
                        </div>
                        <Badge className={whatsappSettings.enable_refill_reminders ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}>
                          {whatsappSettings.enable_refill_reminders ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      className="w-full text-red-600 border-red-300 hover:bg-red-50"
                      onClick={disconnectWhatsapp}
                      disabled={saving}
                    >
                      Disconnect WhatsApp
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={saveWhatsappSettings} className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-green-800 text-sm">Setup Twilio WhatsApp</p>
                          <p className="text-xs text-green-700 mt-1">
                            Get your credentials from{' '}
                            <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">
                              Twilio Console
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="twilio_sid">Account SID</Label>
                      <Input
                        id="twilio_sid"
                        data-testid="twilio-account-sid"
                        placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        value={whatsappForm.twilio_account_sid}
                        onChange={function(e) { setWhatsappForm({ ...whatsappForm, twilio_account_sid: e.target.value }); }}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="twilio_token">Auth Token</Label>
                      <div className="relative">
                        <Input
                          id="twilio_token"
                          data-testid="twilio-auth-token"
                          type={showWASecret ? 'text' : 'password'}
                          placeholder="Enter your auth token"
                          value={whatsappForm.twilio_auth_token}
                          onChange={function(e) { setWhatsappForm({ ...whatsappForm, twilio_auth_token: e.target.value }); }}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={function() { setShowWASecret(!showWASecret); }}
                        >
                          {showWASecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="twilio_number">WhatsApp Number (optional)</Label>
                      <Input
                        id="twilio_number"
                        data-testid="twilio-whatsapp-number"
                        placeholder="whatsapp:+14155238886 (sandbox default)"
                        value={whatsappForm.twilio_whatsapp_number}
                        onChange={function(e) { setWhatsappForm({ ...whatsappForm, twilio_whatsapp_number: e.target.value }); }}
                      />
                    </div>
                    
                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-600" />
                          <Label htmlFor="enable_invoices" className="text-sm cursor-pointer">Enable Invoice Sending</Label>
                        </div>
                        <Switch
                          id="enable_invoices"
                          checked={whatsappForm.enable_invoice_sending}
                          onCheckedChange={function(checked) { setWhatsappForm({ ...whatsappForm, enable_invoice_sending: checked }); }}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-slate-600" />
                          <Label htmlFor="enable_reminders" className="text-sm cursor-pointer">Enable Refill Reminders</Label>
                        </div>
                        <Switch
                          id="enable_reminders"
                          checked={whatsappForm.enable_refill_reminders}
                          onCheckedChange={function(checked) { setWhatsappForm({ ...whatsappForm, enable_refill_reminders: checked }); }}
                        />
                      </div>
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full bg-green-600 hover:bg-green-700"
                      disabled={saving}
                      data-testid="connect-whatsapp-btn"
                    >
                      {saving ? 'Connecting...' : 'Connect WhatsApp'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">WhatsApp Features</CardTitle>
                <CardDescription>What you get with Twilio WhatsApp integration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy-900">Digital Invoices</h4>
                    <p className="text-sm text-slate-500">Send itemized invoices to customers via WhatsApp</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bell className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy-900">Refill Reminders</h4>
                    <p className="text-sm text-slate-500">Automatic reminders based on medication supply duration</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Send className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy-900">Instant Delivery</h4>
                    <p className="text-sm text-slate-500">Messages delivered instantly via WhatsApp</p>
                  </div>
                </div>
                
                <div className="mt-6 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">
                    <strong>Note:</strong> You need a Twilio account with WhatsApp sandbox or Business API access. Start with the free sandbox for testing.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Settings;
